/**
 * @module @yimoka/i18n/remote/backend
 * @summary i18next Backend 插件
 * @author keep
 * @since 2026-05-19
 */

import type { BackendModule, InitOptions, NewableModule, ReadCallback, Services, i18n } from 'i18next';

import { resolveNamespaceWithFallback } from '../bundled/merge';
import type { BundledRegistry } from '../bundled/registry';
import type { FetchRemoteBundle, RemoteSharedState } from '../types';

import { applyBundleToI18n } from './apply-bundle';
import {
  fetchRemoteBundle,
  isRemoteFailed,
  readMemoryCache,
  type FetchBundleResult,
  writeMemoryCache,
} from './fetch-bundle';

/** Backend 工厂配置 */
export interface CreateI18nBackendOptions {
  registry: BundledRegistry;
  fetchRemote: FetchRemoteBundle;
  state: RemoteSharedState;
  memoryCacheTtlMs: number;
  onLoadingChange?: (loading: boolean) => void;
}

const resolveBundleNamespace = (
  registry: BundledRegistry,
  language: string,
  namespace: string,
  bundle: Record<string, unknown>,
): Record<string, unknown> | null => {
  const nsData = bundle[namespace];
  if (!nsData || typeof nsData !== 'object') {
    return null;
  }
  return resolveNamespaceWithFallback(
    registry,
    language,
    namespace,
    nsData as Record<string, unknown>,
  );
};

const readCachedNamespace = (
  state: RemoteSharedState,
  language: string,
  namespace: string,
  memoryCacheTtlMs: number,
  registry: BundledRegistry,
): Record<string, unknown> | null => {
  const cached = readMemoryCache(state, language, memoryCacheTtlMs);
  if (!cached) {
    return null;
  }
  return resolveBundleNamespace(registry, language, namespace, cached);
};

const resolveRemoteNamespace = (
  result: FetchBundleResult,
  i18nInstance: i18n | null,
  language: string,
  namespace: string,
  registry: BundledRegistry,
  state: RemoteSharedState,
): Record<string, unknown> | null => {
  if (result.failed || !result.bundle) {
    return null;
  }

  writeMemoryCache(state, language, result.bundle);
  if (i18nInstance) {
    applyBundleToI18n(i18nInstance, language, result.bundle, registry);
  }
  return resolveBundleNamespace(registry, language, namespace, result.bundle);
};

/**
 * 创建 i18next Backend 模块类。
 *
 * @param options - 配置
 * @returns Backend 类，供 i18next.use()
 */
export function createI18nBackend(options: CreateI18nBackendOptions): NewableModule<BackendModule> {
  const { registry, fetchRemote, state, memoryCacheTtlMs, onLoadingChange } = options;

  class RemoteLangBackend implements BackendModule {
    static type = 'backend' as const;

    type = 'backend' as const;

    /** i18next 实例引用，read 时写入 bundle */
    i18n: i18n | null = null;

    init(_services: Services, _backendOptions: object, i18nextOptions: InitOptions): void {
      this.i18n = (i18nextOptions as InitOptions & { i18n?: i18n }).i18n ?? null;
    }

    /**
     * 读取 namespace 资源。
     *
     * @param language - 语种短码
     * @param namespace - namespace
     * @param callback - i18next 回调
     */
    read(language: string, namespace: string, callback: ReadCallback): void {
      void this.readAsync(language, namespace, callback);
    }

    private async readAsync(language: string, namespace: string, callback: ReadCallback): Promise<void> {
      const bundled = registry.getNamespace(language, namespace);

      if (isRemoteFailed(state, language)) {
        callback(null, bundled);
        return;
      }

      const cached = readCachedNamespace(state, language, namespace, memoryCacheTtlMs, registry);
      if (cached) {
        callback(null, cached);
        return;
      }

      onLoadingChange?.(true);
      try {
        const result = await fetchRemoteBundle(fetchRemote, language, { i18nextLng: language }, state);

        const remote = resolveRemoteNamespace(result, this.i18n, language, namespace, registry, state);
        callback(null, remote ?? bundled);
      } catch {
        callback(null, bundled);
      } finally {
        onLoadingChange?.(false);
      }
    }
  }

  return RemoteLangBackend;
}
